import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ChatEntry } from 'dragonmouse-shared/interfaces';
// It is actually quite intricate to get the scroll behavior we want:
// When the user submits a prompt, we scroll so it's at the top of the screen (leaving space underneath)
// The space persists -- meaning that we can scroll up and down and it remains
// Well the space persists until the system's chat responses appear -- they responses gobble up the empty space!
// If the system's responses don't fill the empty space, a little bit of whitespace is left, the user can
// scroll up and down and that little bit of whitespace remains.
//
// This is the standard but implementing it is pretty intricate
// To get the scroll view to scroll we need to manually inject some space -- outside of the React loop
// so it can all happen before the next render. That's why I use useLayoutEffect.
//
// I talked to some LLMs who were like "here's the easy trick everyone uses" but it always involved naively
// calling scrollIntoView without adding empty space. No variation of that worked for me. But doing the math
// directly here is working very robustly at present -- ss
//
// I did have to use this magic number which is needed to avoid adding a bit TOO much space.
// Perhaps it's needed because padding isn't included in some computations?
// I didn't dig in too much because it's working well and I don't care much beyond that until is starts to fail (sorry) - ss
const MAGIC_NUMBER = 18;

// Module-level cache of scroll state per chatId. Lets us restore the user's scroll
// position when this hook's component unmounts and remounts (e.g. entering/exiting
// prototype mode), instead of snapping back to the bottom.
//
// We save scrollBottom (distance from the end of content) rather than scrollTop so
// the restore is invariant to two things that routinely change between save and
// restore: clientHeight (an upgrade-DS banner appearing above the input) and
// content prepended above (parent chat fetched async). scrollHeight is saved too
// so we can tell when async content has finished loading before revealing.
const savedScrollPositions = new Map<string, { scrollBottom: number; scrollHeight: number }>();

interface UseChatScrollOptions {
	containerRef: React.RefObject<HTMLDivElement>;
	spacerRef: React.RefObject<HTMLDivElement>;
	entries: ChatEntry[];
	activeChatId: string | null;
	lastChatMessage: string | null;
	shouldSkipAutoScroll?: () => boolean;
	isInitialized?: boolean; // Whether parent is ready for initial scroll on load
}

interface UseChatScrollResult {
	isScrolledToBottom: boolean;
	isRestoring: boolean;
	onJumpToBottom: () => void;
}

export function useChatScroll({
	containerRef,
	spacerRef,
	entries,
	activeChatId,
	lastChatMessage,
	shouldSkipAutoScroll,
	isInitialized,
}: UseChatScrollOptions): UseChatScrollResult {
	const [isScrolledToBottom, setIsScrolledToBottom] = useState(true);
	const [isRestoring, setIsRestoring] = useState(false);
	const originalSpacerHeight = useRef<number>(0);
	const spacerHeight = useRef<number>(0);

	// Tracks activeChatId for the scroll listener closure without re-subscribing.
	const activeChatIdRef = useRef(activeChatId);
	activeChatIdRef.current = activeChatId;

	// While we're restoring a saved position, async content (e.g. parent chat) can load and
	// trigger overflow-anchor adjustments. Those fire scroll events that would otherwise
	// clobber the cached position. Gate scroll-listener saves on this timestamp.
	const suppressSaveUntilRef = useRef(0);

	const entriesLength = entries?.length ?? 0;

	// If last message is a user message, scroll so user message appears at top of chat panel.
	// IMPORTANT: Only depend on entriesLength, NOT on entries array itself. The entries array
	// gets a new reference on every Redux update (even when content is unchanged), which would
	// cause spurious scroll-to-top behavior. We only want this to fire when a new entry is added.
	// containerRef/spacerRef are stable refs and don't need to be dependencies.
	// eslint-disable-next-line react-hooks/exhaustive-deps
	useLayoutEffect(() => {
		if (!entries || entriesLength === 0) return;

		if (entries[entriesLength - 1].kind === 'user') {
			const messages = containerRef.current?.querySelectorAll('.user-message');
			const lastUserMessage = messages?.[messages.length - 1];
			if (lastUserMessage) {
				// make the spacer's height be that of the visible area minus that of the user message
				const visibleAreaHeight = containerRef.current?.clientHeight;
				const userMessageHeight = lastUserMessage.clientHeight;
				if (visibleAreaHeight && spacerRef.current) {
					const H = visibleAreaHeight - userMessageHeight - MAGIC_NUMBER;
					spacerRef.current.style.height = `${H}px`;
					spacerHeight.current = H;
					// store this so we can chip away at it as the last system message grows
					originalSpacerHeight.current = H;
				}
				// since we forcibly sized the spacer, voila! our scrolling works
				if (containerRef.current && lastUserMessage) {
					// Get the offsetTop of the message relative to the container
					const messageOffset = (lastUserMessage as HTMLElement).offsetTop;
					containerRef.current.style.scrollBehavior = 'smooth';
					containerRef.current.scrollTop = messageOffset;
				}
			}
		}
	}, [entriesLength]);

	// Whenever the last entry is a system message, eat away at the spacer height as system message grows.
	// This runs regardless of scroll position - we're just reducing empty space, not moving content.
	// IMPORTANT: Only depend on lastChatMessage and entriesLength, NOT on entries array itself.
	// Same reasoning as above - entries array reference changes on every Redux update, causing
	// spurious spacer adjustments and unwanted scroll behavior.
	// containerRef/spacerRef are stable refs and don't need to be dependencies.
	// eslint-disable-next-line react-hooks/exhaustive-deps
	useLayoutEffect(() => {
		if (!entries || entriesLength === 0) return;
		if (spacerHeight.current > 0 && entries[entriesLength - 1].kind === 'system') {
			const messages = containerRef.current?.querySelectorAll('.system-message');
			const lastSystemMessage = messages?.[messages.length - 1];
			if (lastSystemMessage) {
				const systemMessageHeight = lastSystemMessage.clientHeight;
				if (systemMessageHeight && spacerRef.current) {
					// remove the height that's been added by the system message
					const newSpacerHeight = Math.max(originalSpacerHeight.current - systemMessageHeight, 0);
					spacerHeight.current = newSpacerHeight;
					spacerRef.current.style.height = `${spacerHeight.current}px`;
				}
			}
		}
	}, [lastChatMessage, entriesLength]);

	// When chat id changes (or the parent signals init), restore the saved position if we
	// have one for this chat, otherwise scroll to the bottom. useLayoutEffect ensures this
	// happens synchronously before paint — prevents a flicker where the browser briefly
	// paints the container at its default scrollTop before we correct it.
	// IMPORTANT: Omit shouldSkipAutoScroll from deps - it's a callback that gets recreated each render,
	// but we only want to scroll when chatId or initialization state actually changes.
	// containerRef/spacerRef are stable refs and don't need to be dependencies.
	// eslint-disable-next-line react-hooks/exhaustive-deps
	useLayoutEffect(() => {
		if (shouldSkipAutoScroll && shouldSkipAutoScroll()) return;
		if (!isInitialized) return; // Wait for scene/design system to be initialized
		if (!containerRef.current) return;

		// Always prefer a saved position if we have one for this chat. Idempotent across
		// React strict-mode's double-invoke (setup → cleanup → setup) because every run
		// restores to the same value.
		const saved = activeChatId ? savedScrollPositions.get(activeChatId) : undefined;

		if (saved !== undefined) {
			// Suppress scroll-listener saves during the restore window so overflow-anchor
			// adjustments from async content loading don't overwrite the cached value.
			suppressSaveUntilRef.current = Date.now() + 2000;

			const container = containerRef.current;
			// scrollBottom-based target: computed from the current scrollHeight and
			// clientHeight, so changes in either (parent chat prepending, banner
			// appearing) produce the correct scrollTop for the same semantic position.
			const applyTarget = () => {
				if (!containerRef.current) return;
				const { scrollHeight, clientHeight } = containerRef.current;
				containerRef.current.style.scrollBehavior = 'auto';
				containerRef.current.scrollTop = Math.max(
					0,
					scrollHeight - clientHeight - saved.scrollBottom
				);
			};

			// Near-bottom positions: the tail of the content is the same at every
			// intermediate scrollHeight, so we can apply immediately and pin to the new
			// bottom as content grows — no hide needed.
			if (saved.scrollBottom < 2) {
				applyTarget();
				setIsScrolledToBottom(true);
				const deadline = Date.now() + 1500;
				let rafId = requestAnimationFrame(function pin() {
					applyTarget();
					if (Date.now() > deadline) return;
					rafId = requestAnimationFrame(pin);
				});
				return () => cancelAnimationFrame(rafId);
			}

			// If content is already at the saved size, restore immediately — common case
			// when swapping between chats whose content hasn't changed this session.
			if (container.scrollHeight >= saved.scrollHeight) {
				applyTarget();
				const { scrollTop, scrollHeight, clientHeight } = container;
				setIsScrolledToBottom(Math.abs(scrollHeight - scrollTop - clientHeight) < 2);
				return;
			}

			// Non-bottom and content still loading: intermediate scrollHeights map
			// scrollBottom to different visible content (activeChat top vs. parent chat
			// middle), so applying now and re-applying later would produce a visible
			// content swap. Hide until scrollHeight reaches the saved size, then apply
			// and reveal.
			const prevVisibility = container.style.visibility;
			container.style.visibility = 'hidden';
			setIsRestoring(true);

			let rafId = 0;
			let done = false;
			const finish = () => {
				if (done) return;
				done = true;
				applyTarget();
				if (containerRef.current) {
					containerRef.current.style.visibility = prevVisibility;
					const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
					setIsScrolledToBottom(Math.abs(scrollHeight - scrollTop - clientHeight) < 2);
				}
				setIsRestoring(false);
			};

			const deadline = Date.now() + 1500;
			const poll = () => {
				if (!containerRef.current) return;
				if (containerRef.current.scrollHeight >= saved.scrollHeight || Date.now() > deadline) {
					finish();
					return;
				}
				rafId = requestAnimationFrame(poll);
			};
			rafId = requestAnimationFrame(poll);

			return () => {
				if (rafId) cancelAnimationFrame(rafId);
				if (!done && containerRef.current) {
					containerRef.current.style.visibility = prevVisibility;
				}
				setIsRestoring(false);
			};
		}

		spacerHeight.current = 0;
		if (spacerRef.current) {
			// if the prior chat left a spacer, clear it
			spacerRef.current.style.height = `${spacerHeight.current}px`;
		}
		// disable smooth scrolling
		containerRef.current.style.scrollBehavior = 'auto';
		containerRef.current.scrollTop = containerRef.current.scrollHeight;
		setIsScrolledToBottom(true);
	}, [activeChatId, isInitialized]);

	// Scroll to bottom when scene/design system first loads. The 500ms delay accounts for
	// thumbnails and other async content that affects scroll height. Not perfect but helps.
	// Skip if we have a saved position for this chat — we'll be restoring that instead.
	// IMPORTANT: Don't depend on shouldSkipAutoScroll (recreated each render). containerRef
	// is a stable ref and doesn't need to be a dependency.
	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(() => {
		if (shouldSkipAutoScroll && shouldSkipAutoScroll()) return;
		if (!isInitialized) return;
		if (activeChatId && savedScrollPositions.has(activeChatId)) return;
		if (!containerRef.current) return;
		// Capture the chat this timer was scheduled for. If the user swaps chats before
		// it fires, the new chat may have a saved position — firing scroll-to-bottom
		// would clobber that restore.
		const capturedId = activeChatId;
		const timeoutId = setTimeout(() => {
			if (!containerRef.current) return;
			if (activeChatIdRef.current !== capturedId) return;
			if (capturedId && savedScrollPositions.has(capturedId)) return;
			containerRef.current.style.scrollBehavior = 'auto';
			containerRef.current.scrollTop = containerRef.current.scrollHeight;
			setIsScrolledToBottom(true);
		}, 500);
		return () => clearTimeout(timeoutId);
	}, [activeChatId, isInitialized]);

	// Add scroll event listener to track scroll position.
	// Empty deps array is intentional - we capture containerRef.current once on mount.
	// The ref object is stable, and we only need to set up the listener once.
	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(() => {
		const container = containerRef.current;
		if (!container) {
			return;
		}
		const handleScroll = () => {
			const { scrollTop, scrollHeight, clientHeight } = container;
			// instead of 1 we use 2 as a test to account for floating point precision issues
			const isAtBottom = Math.abs(scrollHeight - scrollTop - clientHeight) < 2;

			setIsScrolledToBottom(isAtBottom);

			// Skip saving during the post-restore window — overflow-anchor and re-applies
			// fire scroll events that aren't user-initiated and would corrupt the cache.
			if (Date.now() < suppressSaveUntilRef.current) {
				return;
			}

			// Save scrollBottom (distance from end of content) rather than scrollTop —
			// it's invariant to the two things that typically differ between save and
			// restore: clientHeight (banner above the input appearing/disappearing) and
			// content prepended above the saved position (parent chat fetched async).
			const id = activeChatIdRef.current;
			if (id) {
				savedScrollPositions.set(id, {
					scrollBottom: scrollHeight - scrollTop - clientHeight,
					scrollHeight,
				});
			}
		};

		container.addEventListener('scroll', handleScroll);
		return () => container.removeEventListener('scroll', handleScroll);
	}, []);

	// As messages stream in, update isScrolledToBottom state.
	// Only depend on message length changing, not containerRef (stable ref).
	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(() => {
		if (lastChatMessage?.length && containerRef.current) {
			const { scrollTop, scrollHeight, clientHeight } = containerRef.current;
			const isAtBottom = Math.abs(scrollHeight - scrollTop - clientHeight) < 1;
			setIsScrolledToBottom(isAtBottom);
		}
	}, [lastChatMessage?.length]);

	const onJumpToBottom = () => {
		if (containerRef.current) {
			containerRef.current.style.scrollBehavior = 'smooth';
			containerRef.current.scrollTop = containerRef.current.scrollHeight;
			setIsScrolledToBottom(true);
		}
	};

	return {
		isScrolledToBottom,
		isRestoring,
		onJumpToBottom,
	};
}
